import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { api } from "../services/api";
import { useUser } from "../services/user-context";
import { useOnboarding } from "./OnboardingContext";
import { TutorialTooltipStep } from "../components/TutorialTooltip";

interface TutorialContextType {
  /** Current step number (1-based, 0 means not started) */
  currentStep: number;
  /** Whether the tutorial has been completed */
  completed: boolean;
  /** Whether the tutorial is currently skipped (hidden) */
  isSkipped: boolean;
  /** Whether the tutorial is visible */
  isVisible: boolean;
  /** Move to the next step */
  nextStep: () => Promise<void>;
  /** Skip the tutorial – marks it complete so it never appears again (used by "Skip for now" in tooltip) */
  skip: () => void;
  /** Get the current step configuration (filtered by route) */
  getCurrentStepConfig: () => TutorialTooltipStep | null;
  /** Update tutorial step on backend */
  updateStep: (step: number, completed: boolean) => Promise<void>;
  /** Reset the tutorial to step 1 so it plays again */
  resetTutorial: () => Promise<void>;
}

export const TutorialContext = createContext<TutorialContextType | undefined>(undefined);

interface TutorialProviderProps {
  children: React.ReactNode;
  /** Total number of steps in the tutorial */
  totalSteps: number;
  /** Configuration for each step */
  steps: TutorialTooltipStep[];
  /** Whether to auto-start the tutorial when component mounts */
  autoStart?: boolean;
}

export function TutorialProvider({
  children,
  totalSteps,
  steps,
  autoStart = true,
}: TutorialProviderProps) {
  const { userInfo, setUserInfo } = useUser() as any;
  const { showOnboarding: questionnaireModalVisible } = useOnboarding();
  const [isSkipped, setIsSkipped] = useState(false);
  
  // Initialize state - will be updated when userInfo loads
  const [currentStep, setCurrentStep] = useState(0);
  const [completed, setCompleted] = useState(false);

  // Tutorial is visible if:
  // - Questionnaire modal is not currently open (hide tooltip whenever questionnaire is shown)
  // - Not completed
  // - Not skipped
  // - Current step is within valid range (1 to totalSteps)
  // - Auto-start is enabled or step > 0
  const isVisible =
    !questionnaireModalVisible &&
    !completed &&
    !isSkipped &&
    currentStep > 0 &&
    currentStep <= totalSteps &&
    (autoStart || currentStep > 0);

  const updateStep = useCallback(async (step: number, isCompleted: boolean) => {
    try {
      // Update userInfo optimistically so the sync effect doesn't revert local state (avoids tooltip blink on first/last step)
      if (setUserInfo) {
        setUserInfo((prev: any) => ({
          ...prev,
          helpTooltipTutorialStep: step,
          helpTooltipTutorialCompleted: isCompleted,
        }));
      }
      await api.updateHelpTooltipTutorial({
        currentStep: step,
        completed: isCompleted,
      });
    } catch (error) {
      console.error("Failed to update tutorial step:", error);
    }
  }, [setUserInfo]);

  const nextStep = useCallback(async () => {
    if (currentStep >= totalSteps) {
      setCompleted(true);
      await updateStep(currentStep, true);
    } else {
      const nextStepNumber = currentStep + 1;
      setCurrentStep(nextStepNumber);
      await updateStep(nextStepNumber, false);
    }
  }, [currentStep, totalSteps, updateStep]);

  // "Skip for now": hide immediately with isSkipped (init effect never overwrites it), then persist.
  const skip = useCallback(async () => {
    setIsSkipped(true);
    await updateStep(currentStep > 0 ? currentStep : totalSteps, true);
    setCompleted(true);
  }, [currentStep, totalSteps, updateStep]);

  const resetTutorial = useCallback(async () => {
    setIsSkipped(false);
    setCompleted(false);
    setCurrentStep(1);
    await updateStep(1, false);
  }, [updateStep]);

  const getCurrentStepConfig = useCallback((): TutorialTooltipStep | null => {
    if (!isVisible || currentStep < 1 || currentStep > steps.length) {
      return null;
    }

    const step = steps[currentStep - 1];
    if (!step) {
      return null;
    }

    // Filter by route if specified (skip when showWhenElementOnPage: tooltip shows when anchor is on page)
    if (!step.showWhenElementOnPage && step.route) {
      const currentPath = window.location.pathname;
      if (!currentPath.startsWith(step.route)) {
        return null;
      }
    }

    return step;
  }, [isVisible, currentStep, steps]);

  // Sync from userInfo when it changes (e.g. initial load, or update from another tab).
  // Intentionally omit currentStep/completed from deps so we do not run when we optimistically
  // advance — otherwise we'd revert to stale userInfo before setUserInfo has flushed.
  useEffect(() => {
    if (!userInfo?.id) {
      return;
    }

    const userStep = userInfo?.helpTooltipTutorialStep;
    const userCompleted = userInfo?.helpTooltipTutorialCompleted ?? false;

    if (userStep !== undefined && userStep !== currentStep) {
      setCurrentStep(userStep);
    }

    if (userCompleted !== completed) {
      setCompleted(userCompleted);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only sync when userInfo changes, not when we advance locally
  }, [userInfo?.helpTooltipTutorialStep, userInfo?.helpTooltipTutorialCompleted]);

  // Auto-start tutorial if enabled and user hasn't started/completed.
  // updateStep omitted from deps: we only want to run when conditions change, not when the callback identity changes.
  useEffect(() => {
    if (!userInfo?.id) {
      return;
    }

    if (userInfo?.helpTooltipTutorialStep !== undefined && userInfo?.helpTooltipTutorialStep > 0) {
      return;
    }

    if (userInfo?.helpTooltipTutorialCompleted) {
      return;
    }

    if (autoStart && currentStep === 0 && !completed && !isSkipped) {
      setCurrentStep(1);
      updateStep(1, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- condition-driven; updateStep is intentionally omitted
  }, [userInfo?.helpTooltipTutorialStep, userInfo?.helpTooltipTutorialCompleted, autoStart, currentStep, completed, isSkipped]);

  const value: TutorialContextType = {
    currentStep,
    completed,
    isSkipped,
    isVisible,
    nextStep,
    skip,
    getCurrentStepConfig,
    updateStep,
    resetTutorial,
  };

  return (
    <TutorialContext.Provider value={value}>
      {children}
    </TutorialContext.Provider>
  );
}

export function useTutorial() {
  const context = useContext(TutorialContext);
  if (context === undefined) {
    throw new Error("useTutorial must be used within a TutorialProvider");
  }
  return context;
}
